// src/views/admin/campaigns/index.jsx
import { useToast, Button } from '@chakra-ui/react';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import CampaignsTable from '../../../components/Campaigns/CampaignsTable.js';
import CampaignDetailModal from '../../../components/Campaigns/CampaignDetailModal.js';
import DeleteCampaignModal from '../../../components/Campaigns/DeleteCampaignModal.js';
import { columnsDataCampaigns } from './variables/columnsDataCampaigns.js';
import React, { useEffect, useState } from 'react';
import { getData, postData, putData, deleteData } from '../../../utils/httpClient.js';
import { useSearch } from '../../../contexts/SearchContext';
import { useResetPageOnFilterChange } from '../../../hooks/useResetPageOnFilterChange.js';
import { useTableSorting } from '../../../hooks/useTableSorting.js';
import authService from '../../../services/authService.js';

export default function Campaigns() {
  const isReadOnly = authService.isMechanic();
  const { searchQuery, resetListFilters } = useSearch(); 
  const [data, setData] = useState([]); 
  const [page, setPage] = useState(0); 
  const [totalPages, setTotalPages] = useState(0); 
  const [detailModalState, setDetailModalState] = useState({ 
    isOpen: false, 
    campaign: null, 
    isNew: false 
  });
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    campaign: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10); 
  const toast = useToast();
  const { sorting, setSorting, sortBy, sortDir, appendSortParams } = useTableSorting();

  useResetPageOnFilterChange(setPage, page, [searchQuery, rowsPerPage, sortBy, sortDir]);

  const loadData = async (searchTerm = '') => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        data: searchTerm || '',
      });
      appendSortParams(params);
      const response = await getData(`/campaigns?${params.toString()}`);
      
      setData(response?.campaigns || []); 
      setTotalPages(Math.ceil((response?.total || 0) / rowsPerPage));
    } catch (error) {
      toast({
        title: 'Error al obtener campañas',
        description: error.response?.data?.message || 'Error del servidor',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData(searchQuery);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery, rowsPerPage, sortBy, sortDir]);

  useEffect(() => {
    resetListFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNewCampaignModal = () => {
    setDetailModalState({ 
      isOpen: true, 
      campaign: null, 
      isNew: true 
    });
  };

  const openViewModal = (campaign) => {
    setDetailModalState({
      isOpen: true,
      campaign,
      isNew: false,
    });
  };

  const closeDetailModal = () => {
    setDetailModalState({ 
      isOpen: false, 
      campaign: null, 
      isNew: false 
    });
  };

  const openDeleteModal = (campaign) => {
    setDeleteModalState({
      isOpen: true,
      campaign,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModalState({
      isOpen: false,
      campaign: null,
    });
  };

  const handleCreateCampaign = async (newCampaign) => {
    try {
      await postData('/campaigns', newCampaign);
      await loadData(searchQuery);
      toast({
        title: 'Campaña creada',
        description: `Campaña ${newCampaign.code} creada exitosamente`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      throw error; // Re-throw para que el modal maneje el error
    }
  };

const handleUpdateCampaign = async (campaignId, campaignData) => {
  try {
    const response = await putData(`/campaigns/${campaignId}`, campaignData);
    
    // Recargar la lista para obtener los datos actualizados
    await loadData(searchQuery);
    
    toast({
      title: 'Campaña actualizada',
      description: 'Los cambios se guardaron correctamente',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    
    return response; // Devolver la respuesta para que el modal pueda usarla
  } catch (error) {
    toast({
      title: 'Error al actualizar',
      description: error.message || 'No se pudo actualizar la campaña',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    throw error; // Re-throw para que el modal maneje el error
  }
};  const handleDeleteCampaign = async (campaignId) => {
    try {
      setIsLoading(true);
      await deleteData(`/campaigns/${campaignId}`);
      await loadData(searchQuery);
      closeDeleteModal();
      toast({
        title: 'Campaña eliminada',
        description: 'La campaña ha sido desactivada correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error al eliminar campaña',
        description: error.response?.data?.message || 'Error del servidor',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AdminPageShell
      title="Campañas"
      actions={!isReadOnly ? (
        <Button variant="adminPrimary" size="sm" onClick={openNewCampaignModal}>
          Nueva campaña
        </Button>
      ) : null}
    >
      <CampaignsTable
          columnsData={columnsDataCampaigns}
          data={data} 
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          pageSize={rowsPerPage}
          setPageSize={setRowsPerPage}
          onView={openViewModal}
          onDelete={openDeleteModal}
          readOnly={isReadOnly}
          sorting={sorting}
          onSortingChange={setSorting}
        />


      {/* Modal de detalle/edición */}
      <CampaignDetailModal
        isOpen={detailModalState.isOpen}
        onClose={closeDetailModal}
        campaign={detailModalState.campaign}
        isNew={detailModalState.isNew}
        onCreate={handleCreateCampaign}
        onUpdate={handleUpdateCampaign}
        onDataChanged={() => loadData(searchQuery)} // Nueva función para recargar datos
        readOnly={isReadOnly}
      />

      {/* Modal de confirmación de eliminación */}
      {!isReadOnly && (
        <DeleteCampaignModal
          isOpen={deleteModalState.isOpen}
          onClose={closeDeleteModal}
          campaign={deleteModalState.campaign}
          onConfirm={handleDeleteCampaign}
          isLoading={isLoading}
        />
      )}
    </AdminPageShell>
  );
}
